/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Click-to-dial, droppable next to any phone number in the CRM.
 *
 * Renders nothing when telephony is not set up, so it can be placed on record
 * pages unconditionally without those pages needing to know anything about the
 * call center. Passing the record context means the call lands on that
 * record's timeline when the agent completes wrap-up.
 *
 * Usage:
 *   <CallButton phone={contact.phone} parentTable="contacts" parentId={contact.id}
 *               contactId={contact.id} accountId={contact.account_id} />
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, CircularProgress, IconButton, Snackbar, Tooltip } from "@mui/material";
import { Call } from "@mui/icons-material";
import MDButton from "components/MDButton";
import {
  commonIconButtonStyles,
  commonIconStyles,
} from "assets/theme/components/iconButtonStyles";
import { useTelephony } from "telephony/TelephonyContext";
import { CALL_STATE, PRESENCE_META } from "telephony/callStates";

/**
 * The dialling half of CallButton, shared with v2 phone links
 * (components/v2/ContactValue.js) so both offer and refuse a call for the
 * same reasons and file it against the same record.
 * `available` is false when there is no telephony or no number.
 */
export function useClickToCall({ phone, context, onError }) {
  const { enabled, callState, placeCall, isLeader, presence, registered } =
    useTelephony();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const available = Boolean(enabled && phone);
  const onCall = callState !== CALL_STATE.IDLE;
  // Being reachable is a precondition for dialling out, not just for being
  // rung: an agent who is Offline has no registered Device to originate from.
  // Greyed out with a reason rather than hidden, so the agent can see the
  // action exists and what to do about it.
  const isAvailable = presence === "available";
  const disabled = busy || onCall || !isLeader || !registered || !isAvailable;

  const presenceLabel = PRESENCE_META[presence]?.label || "Offline";
  const tooltip = !isLeader
    ? "The softphone is active in another browser tab"
    : onCall
    ? "You are already on a call"
    : !registered
    ? "Your softphone is not connected. Open the Call Console to sign in."
    : !isAvailable
    ? `You are ${presenceLabel.toLowerCase()}. Go Available to place calls.`
    : `Call ${phone}`;

  const dial = async () => {
    setBusy(true);
    setError("");
    try {
      await placeCall({
        to: phone,
        context: {
          parent_table: context?.parentTable,
          parent_id: context?.parentId,
          account_id: context?.accountId,
          contact_id: context?.contactId,
          lead_id: context?.leadId,
          deal_id: context?.dealId,
          case_id: context?.caseId,
        },
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || "The call could not be placed.";
      // A table of 50 rows would otherwise mount 50 Snackbars. Where the host
      // gives us somewhere to report to, use it; otherwise fall back to our own.
      if (onError) onError(message);
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  return { available, disabled, busy, tooltip, dial, error, clearError: () => setError("") };
}

export default function CallButton({
  phone,
  parentTable,
  parentId,
  accountId,
  contactId,
  leadId,
  dealId,
  caseId,
  variant,
  size,
  label,
  onError,
  sx,
  tone,
}) {
  const { available, disabled, busy, tooltip, dial, error, clearError } = useClickToCall({
    phone,
    context: { parentTable, parentId, accountId, contactId, leadId, dealId, caseId },
    onError,
  });

  // Nothing to offer: no telephony, or no number to dial. Hidden rather than
  // disabled, because a permanently dead icon on a tenant without the module
  // is noise -- and this is what lets the button be dropped onto record pages
  // and list rows unconditionally.
  if (!available) return null;

  // Detail-page headers sit on a dark gradient band where the default
  // `color="info"` blue is barely legible, and where every sibling action is a
  // dark pill with a white glyph. `tone="header"` borrows that pill so the call
  // action stops reading as a different class of control.
  const isHeader = tone === "header";

  // Left at MUI's default "medium" in header tone, deliberately. `size="small"`
  // carries `& .MuiSvgIcon-root { font-size: 1.125rem }`, which beats the
  // fontSize in commonIconStyles and renders the phone at 18px next to
  // siblings at 24px -- close enough to look like a mistake rather than a
  // choice, which is how it shipped the first time.
  const control =
    variant === "icon" ? (
      <span>
        <IconButton
          onClick={dial}
          disabled={disabled}
          size={isHeader ? undefined : size}
          color={isHeader ? undefined : "info"}
          sx={isHeader ? { ...commonIconButtonStyles, ...sx } : sx}
        >
          {busy ? (
            <CircularProgress size={20} sx={isHeader ? { color: commonIconStyles.color } : undefined} />
          ) : (
            <Call {...(isHeader ? { sx: commonIconStyles } : { fontSize: "small" })} />
          )}
        </IconButton>
      </span>
    ) : (
      <span>
        <MDButton
          variant="gradient"
          color="info"
          size={size}
          onClick={dial}
          disabled={disabled}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Call />}
        >
          {label || "Call"}
        </MDButton>
      </span>
    );

  if (onError) {
    return (
      <Tooltip title={tooltip} placement="top">
        {control}
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={tooltip} placement="top">
        {control}
      </Tooltip>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={clearError}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

CallButton.propTypes = {
  phone: PropTypes.string,
  parentTable: PropTypes.string,
  parentId: PropTypes.number,
  accountId: PropTypes.number,
  contactId: PropTypes.number,
  leadId: PropTypes.number,
  dealId: PropTypes.number,
  caseId: PropTypes.number,
  variant: PropTypes.oneOf(["icon", "button"]),
  size: PropTypes.string,
  label: PropTypes.string,
  onError: PropTypes.func,
  // Extra IconButton styling, merged after the tone preset.
  sx: PropTypes.object,
  // "header" adopts the dark pill + white glyph used by sibling header actions.
  tone: PropTypes.oneOf(["default", "header"]),
};

CallButton.defaultProps = {
  phone: null,
  parentTable: null,
  parentId: null,
  accountId: null,
  contactId: null,
  leadId: null,
  dealId: null,
  caseId: null,
  variant: "icon",
  size: "small",
  label: null,
  onError: null,
  sx: undefined,
  tone: "default",
};
